// NodeIntellicaHeader.tsx
import React, { useState, useRef, useEffect } from 'react'
import styles from '~/styles/NodeIntellica.module.css'
import NodeBaseActions from '../../base/base-actions/NodeBaseActions'
import NodeCustomActions from '../../custom-actions/NodeCustomActions'
import { NodeIntellicaData } from '../NodeIntellica'

interface NodeIntellicaHeaderProps {
  id: string
  data: NodeIntellicaData
  syncNodeData: (newData: Partial<NodeIntellicaData>) => void
  assignedEmails?: string[] | string;
}

const NodeIntellicaHeader: React.FC<NodeIntellicaHeaderProps> = ({
  id,
  data,
  syncNodeData,
}) => {
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [editingTitleValue, setEditingTitleValue] = useState(
    data.label || 'Intellica'
  )
  const [isLocked, setIsLocked] = useState(data.isLocked || false)
  const [isMinimized, setIsMinimized] = useState(data.isMinimized || false)
  const [assignedEmails, setAssignedEmails] = useState(
    data.assignedEmails || ''
  )
  const titleInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus()
      titleInputRef.current.select()
    }
  }, [isEditingTitle])

  const handleTitleSave = () => {
    syncNodeData({ label: editingTitleValue })
    setIsEditingTitle(false)
  }

  const handleTitleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleTitleSave()
    else if (e.key === 'Escape') {
      setEditingTitleValue(data.label || 'Intellica')
      setIsEditingTitle(false)
    }
  }

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this intellica?')) {
      ;(window as any).__flowDeleteNode(id)
    }
  }

  const handleDuplicate = () => {
    ;(window as any).__flowDuplicateNode(id)
  }

  const handleLock = () => {
    const newState = !isLocked
    setIsLocked(newState)
    syncNodeData({ isLocked: newState })
  }

  const handleMinimize = () => {
    const newState = !isMinimized
    setIsMinimized(newState)
    syncNodeData({ isMinimized: newState })
  }

  const customButtons = [
    {
      id: '1',
      onClick: () => {
        const emails = window.prompt(
          'Enter emails separated by space',
          assignedEmails
        )
        if (emails !== null) {
          setAssignedEmails(emails)
          syncNodeData({ assignedEmails: emails })
        }
      },
      icon: <i className="pi pi-user-plus" style={{ fontSize: '1rem' }}></i>,
      tooltip: 'Assign user(s)',
    },
  ]

  const emailsArray = Array.isArray(assignedEmails) 
  ? assignedEmails 
  : (assignedEmails ? assignedEmails.trim().split(/\s+/).filter(Boolean) : []);

  return (
    <div className={styles.nodeHeader}>
      <h2
        className={styles.title}
        onClick={() => !isLocked && setIsEditingTitle(true)}
        style={{ cursor: isLocked ? 'default' : 'pointer' }}
      >
        {isEditingTitle && !isLocked ? (
          <input
            ref={titleInputRef}
            type="text"
            value={editingTitleValue}
            onChange={(e) => setEditingTitleValue(e.target.value)}
            onBlur={handleTitleSave}
            onKeyDown={handleTitleKeyPress}
            className={`${styles.titleInput} nodrag`}
          />
        ) : (
          data.label || 'Intellica'
        )}
      </h2>
      <NodeBaseActions
        onDelete={handleDelete}
        onDuplicate={handleDuplicate}
        onLock={handleLock}
        onUnlock={isLocked}
        onMinimize={handleMinimize}
        onMaximize={isMinimized}
      />
      {!isLocked && <NodeCustomActions buttons={customButtons} nodeId={id} />}
      {emailsArray.length > 0 && !isLocked && (
        <div className={styles.assignedEmails}>
          Assigned to:{' '}
          {emailsArray.map((email) => (
            <span key={email}>{email} </span>
          ))}
        </div>
      )}
    </div>
  )
}

export default NodeIntellicaHeader
